import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { FiX } from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";

type DialogSize = "sm" | "md" | "lg" | "xl";
const WIDTH: Record<DialogSize, string> = {
  sm: "440px",
  md: "560px",
  lg: "1100px",
  xl: "1180px",
};

const fade = keyframes`from { opacity: 0 } to { opacity: 1 }`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.985) }
  to { opacity: 1; transform: none }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: #05070ac9;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${fade} ${(p) => p.theme.motion.base} linear;
  @media (max-width: 600px) {
    padding: 0;
    align-items: stretch;
  }
`;
const Card = styled.section<{ $width: string; $full?: boolean }>`
  width: 100%;
  max-width: ${(p) => p.$width};
  max-height: calc(100svh - 48px);
  height: ${(p) => (p.$full ? "min(860px, calc(100svh - 48px))" : "auto")};
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${(p) => p.theme.colors.surface};
  color: ${(p) => p.theme.colors.text};
  border: 1px solid ${(p) => p.theme.colors.borderLight};
  border-radius: ${(p) => p.theme.radius.dialog};
  box-shadow: 0 30px 100px #0009;
  animation: ${rise} ${(p) => p.theme.motion.dialog} ${(p) => p.theme.motion.ease};
  h2 {
    padding-right: 36px;
    font-size: 22px;
    letter-spacing: -0.03em;
  }
  @media (max-width: 600px) {
    max-width: none;
    max-height: 100svh;
    height: ${(p) => (p.$full ? "100svh" : "auto")};
    margin-top: auto;
    border-radius: 16px 16px 0 0;
    border-bottom: 0;
  }
`;
const Body = styled.div<{ $flush?: boolean; $contain?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow: ${(p) => (p.$contain ? "hidden" : "auto")};
  overscroll-behavior: contain;
  display: grid;
  align-content: start;
  gap: 20px;
  padding: ${(p) => (p.$flush ? "0" : "28px")};
  ${(p) => (p.$contain ? "grid-template-rows: minmax(0, 1fr); align-content: stretch;" : "")}
  @media (max-width: 600px) {
    padding: ${(p) => (p.$flush ? "0" : "20px 16px")};
  }
`;
const Footer = styled.footer`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 28px;
  border-top: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 600px) {
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  }
`;
const Close = styled.button`
  position: absolute;
  right: 14px;
  top: 14px;
  z-index: 2;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: ${(p) => p.theme.colors.textMuted};
  transition:
    background ${(p) => p.theme.motion.fast},
    color ${(p) => p.theme.motion.fast};
  &:hover {
    background: ${(p) => p.theme.colors.surfaceHover};
    color: ${(p) => p.theme.colors.text};
  }
`;
const Discard = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: grid;
  place-items: center;
  padding: 20px;
  background: #0c0e12d9;
  > div {
    width: 100%;
    max-width: 380px;
    display: grid;
    gap: 14px;
    padding: 22px;
    border: 1px solid ${(p) => p.theme.colors.borderLight};
    border-radius: 14px;
    background: ${(p) => p.theme.colors.surfaceHover};
  }
  p {
    color: ${(p) => p.theme.colors.textSecondary};
    font-size: 14px;
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
  }
  button {
    min-height: 40px;
    padding: 8px 14px;
    border-radius: 10px;
    border: 1px solid ${(p) => p.theme.colors.borderLight};
    font-weight: 600;
  }
  button.danger {
    color: ${(p) => p.theme.colors.danger};
    border-color: #ee737340;
  }
  button.primary {
    background: ${(p) => p.theme.colors.primary};
    border-color: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.onPrimary};
  }
`;

export interface DialogProps {
  label: string;
  onClose: () => void;
  /** Устаревший флаг: эквивалент size="lg". */
  wide?: boolean;
  size?: DialogSize;
  /** Закреплённая нижняя панель с действиями; тело прокручивается отдельно. */
  footer?: React.ReactNode;
  /** Есть несохранённые изменения: закрытие спросит подтверждение. */
  dirty?: boolean;
  /** Растянуть на всю доступную высоту (каталоги, редакторы). */
  fill?: boolean;
  /** Без внутренних отступов: содержимое само раскладывает области и их прокрутку. */
  flush?: boolean;
  children: React.ReactNode;
}

export default function Dialog({
  label,
  onClose,
  wide,
  size,
  footer,
  dirty,
  fill,
  flush,
  children,
}: DialogProps) {
  const { t } = useLanguage();
  const ref = useRef<HTMLElement>(null),
    close = useRef(onClose),
    dirtyRef = useRef(!!dirty);
  close.current = onClose;
  dirtyRef.current = !!dirty;
  const [askDiscard, setAskDiscard] = useState(false);
  const askRef = useRef(false);
  askRef.current = askDiscard;

  const requestClose = useCallback(() => {
    if (dirtyRef.current) setAskDiscard(true);
    else close.current();
  }, []);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!ref.current?.contains(document.activeElement)) ref.current?.focus();
    function key(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (askRef.current) setAskDiscard(false);
        else requestClose();
      }
      if (e.key === "Tab") {
        const scope =
          (askRef.current
            ? ref.current?.querySelector<HTMLElement>("[data-discard]")
            : null) || ref.current;
        const nodes = Array.from(
          scope?.querySelectorAll<HTMLElement>(
            'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]',
          ) || [],
        );
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (!first) {
          e.preventDefault();
          return;
        }
        if (!scope?.contains(document.activeElement)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }
        if (
          e.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === ref.current)
        ) {
          e.preventDefault();
          last.focus();
        } else if (
          !e.shiftKey &&
          (document.activeElement === last ||
            document.activeElement === ref.current)
        ) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", key, true);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", key, true);
      previous?.focus();
    };
  }, [requestClose]);

  const width = WIDTH[size || (wide ? "lg" : "md")];
  return createPortal(
    <Overlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <Card
        ref={ref}
        $width={width}
        $full={fill}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        <Close
          type="button"
          onClick={requestClose}
          aria-label={t("common.close_window")}
        >
          <FiX />
        </Close>
        <Body $flush={flush} $contain={flush && fill}>
          {children}
        </Body>
        {footer && <Footer>{footer}</Footer>}
        {askDiscard && (
          <Discard role="alertdialog" aria-label={t("dialog.discard_title")}>
            <div data-discard>
              <h3>{t("dialog.discard_title")}</h3>
              <p>{t("dialog.discard_text")}</p>
              <div className="row">
                <button
                  type="button"
                  className="danger"
                  onClick={() => {
                    setAskDiscard(false);
                    close.current();
                  }}
                >
                  {t("dialog.discard")}
                </button>
                <button
                  type="button"
                  className="primary"
                  autoFocus
                  onClick={() => setAskDiscard(false)}
                >
                  {t("dialog.keep_editing")}
                </button>
              </div>
            </div>
          </Discard>
        )}
      </Card>
    </Overlay>,
    document.body,
  );
}
